import { Component, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { TargetModule } from "@enums/document/TargetModule";
import { TranslocoService, provideTranslocoScope } from "@jsverse/transloco";
import { nameof } from "@methods/jeffs-toolkit";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { HyperlinkModel } from "@models/hyperlinks/hyperlinkmodel";
import { ToastrService } from "ngx-toastr";
import { of, firstValueFrom } from "rxjs";

const HYPERLINK_MANAGER_SCOPE = "shared/hyperlink-manager";

@Component({
    selector: "hyperlink-manager",
    templateUrl: "./hyperlink-manager.component.html",
    styleUrls: ["./hyperlink-manager.component.scss"],
    standalone: false,
    providers: [
        provideTranslocoScope({ scope: HYPERLINK_MANAGER_SCOPE, alias: "hyperlinkManager" }),
    ]
})
export class HyperlinkManagerComponent implements OnInit {
    private _standingDataDS = inject(StandingdataDataService);
    private _toastr = inject(ToastrService);
    private _transloco = inject(TranslocoService);

    @Input() Hyperlinks: HyperlinkModel[] = [];
    @Input() TargetModule: TargetModule;

    @Input() Label: string;
    @Input() Readonly: boolean = false;

    @Output() HyperlinksChanged = new EventEmitter<HyperlinkModel[]>();

    listConfig: GenericListConfig;
    hyperlinkTypes: IdNameCombination[];

    async ngOnInit(): Promise<void> {
        // Every string of this component goes into the generic-list config, so it is read before any
        // transloco pipe renders this scope. Load the scope first; translate() returns the raw key
        // otherwise. The template guards on listConfig, so the first pass renders nothing.
        await firstValueFrom(this._transloco.load(this.scopeLang()));
        await this.getStandingData();
        await this.initListManager();
    }

    private scopeLang(): string {
        return `${HYPERLINK_MANAGER_SCOPE}/${this._transloco.getActiveLang()}`;
    }

    private translate(key: string): string {
        return this._transloco.translate(key, {}, this.scopeLang());
    }

    private async getStandingData(): Promise<void> {
        this.hyperlinkTypes = await firstValueFrom(
            this._standingDataDS.getHyperlinkTypes(this.TargetModule)
        );
    }

    private initListManager(): void {
        this.listConfig = <GenericListConfig>{
            name: this.Label ?? this.translate("label"),
            idProp: nameof<HyperlinkModel>((h) => h.id),
            isSortable: false,

            uiid: "hyperlink-manager",
            uiidAdd: "hyperlink-add",
            uiidDelete: "hyperlink-delete",

            allowAdd: !this.Readonly,
            allowEdit: !this.Readonly,
            allowDelete: !this.Readonly,

            fields: [
                {
                    fieldName: nameof<HyperlinkModel>((h) => h.name),
                    prettyName: this.translate("nameColumn"),
                    fieldType: GenericListFieldType.Text,
                    required: true,
                    uiid: "hyperlink-name",
                },
                {
                    fieldName: nameof<HyperlinkModel>((h) => h.hyperlink),
                    prettyName: this.translate("hyperlinkColumn"),
                    fieldType: GenericListFieldType.TextArea,
                    required: true,
                    uiid: "hyperlink-hyperlink",
                    customValidation: (row) => {
                        var value = row.hyperlink;
                        const whiteSpacePattern = /[\s]/i;
                        const containsWhiteSpace = whiteSpacePattern.test(value);
                        if (containsWhiteSpace) {
                            return {
                                makeRequired: false,
                                validation: this.translate("whitespaceValidation"),
                            };
                        }
                    },
                },
                {
                    fieldName: nameof<HyperlinkModel>((h) => h.hyperlinkTypeId),
                    prettyName: this.translate("hyperlinkTypeColumn"),
                    fieldType: GenericListFieldType.SingleSelect,
                    required: false,
                    uiid: "hyperlink-type",
                    getDataMethod: () => {
                        return of(this.hyperlinkTypes);
                    },
                },
                {
                    fieldName: nameof<HyperlinkModel>((h) => h.description),
                    prettyName: this.translate("descriptionColumn"),
                    fieldType: GenericListFieldType.TextArea,
                    required: false,
                    uiid: "hyperlink-description",
                },
            ],

            data: this.Hyperlinks,
            dataChanged: (data: HyperlinkModel[]) => {
                this.Hyperlinks = data;
                this.HyperlinksChanged.emit(data);
            },

            overviewRowActionsName: this.translate("openActionsColumn"),
            overviewRowActions: [
                {
                    icon: "fas fa-external-link-alt",
                    tooltip: this.translate("openHyperlinkTooltip"),
                    clickEvent: (row: HyperlinkModel) => {
                        if (row.hyperlink.indexOf(":") == -1)
                            this._toastr.warning(this.translate("missingProtocolWarning"));
                        else window.open(row.hyperlink, "_blank");
                    },
                },
            ],
        };
    }
}
